import { Injectable } from '@angular/core';
import { faker } from '@faker-js/faker';
import { delay, map, Observable, of } from 'rxjs';
import { INotification } from 'src/app/core/models/notification';
import { IAcademicClass, IExtracurricular, ISchool } from '../../models/academic';
import { IEvent } from '../../models/event';
import { IFakerEmergencyContact, IFakerGuardian } from '../../models/fakermodels';
import { IResource } from '../../models/resource';
import { IEmergencyContact, IGuardian, IStudent } from '../../models/user';

@Injectable({
  providedIn: 'root'
})
export class GlobalDataService {
  private guardian: IFakerGuardian | null = null;
  private readonly emergency: IFakerEmergencyContact | null = null;

  setData(data: IFakerGuardian) {
    this.guardian = data;
  }

  getGuardian(): IGuardian | null {
    if (!this.guardian) {
      return null;
    }

    const learnerAccess = this.guardian.learners.map(learner => ({
      learnerId: learner.id,
      userType: 'Admin',
      color: learner.colour,
      accountCode: faker.string.alphanumeric(8).toUpperCase(),
      companyCode: faker.string.alphanumeric(6).toUpperCase(),
      schoolId: learner.school.id
    }));

    const guardian = {
      ...this.guardian,
      learners: [],
      learnerAccess: learnerAccess
    };

    return guardian as IGuardian;
  }

  getEmergency(): IEmergencyContact | null {
    if (!this.emergency) {
      return null;
    }

    return {
      name: this.emergency.name,
      surname: this.emergency.surname,
      phoneNumber: this.emergency.phoneNumber,
      email: this.emergency.email,
      countryCode: this.emergency.countryCode,
      relationship: this.emergency.relationship,
      emailType: this.emergency.emailType,
      phoneType: this.emergency.phoneType,
      nationalNumber: this.emergency.nationalNumber,
      phoneId: this.emergency.phoneId,
      emailId: this.emergency.emailId
    };
  }

  getStudents(): IStudent[] | null {
    if (!this.guardian) {
      return null;
    }

    return this.guardian.learners.map(learner => (
      {
        ...learner,
        preferredName: learner.firstname,
        studentNumber: faker.string.numeric(5),
        pictureId: '',
        enrolmentStatus: 'Active',
        enrolmentDocuments: [],
        accountCode: faker.string.alphanumeric(8).toUpperCase(),
        companyCode: faker.string.alphanumeric(6).toUpperCase(),
        schoolId: learner.school.id,
        classIds: learner.classes.map(c => c.id),
        extracurricularIds: learner.extracurriculars.map(e => e.id),
        attendance: learner.attendance.map(att => ({
          ...att,
          academicClassId: faker.helpers.arrayElement(learner.classes.map(c => c.id))
        })),
      }));
  }

  getSchools(): ISchool[] | null {
    if (!this.guardian) {
      return null;
    }

    const schools: ISchool[] = [];

    this.guardian.learners.forEach(learner => {
      const fakerSchool = learner.school;

      const school: ISchool = {
        id: fakerSchool.id,
        name: fakerSchool.name,
        location: fakerSchool.location,
        eventIds: fakerSchool.events.map(event => event.id),
        executives: fakerSchool.executives
      };

      schools.push(school)
    });

    return schools
  }

  getEvents(): IEvent[] | [] {
    if (!this.guardian) {
      return [];
    }

    const schoolEvents = this.guardian.learners.flatMap(learner =>
      learner.school.events.map(event => ({
        ...event,
        colour: '#8ba8fc',
        relatedField: {
          type: 'School',
          id: learner.school.id
        }
      }))
    );

    const classEvents = this.guardian.learners.flatMap(learner =>
      learner.classes.flatMap(c =>
        c.events.map(event => ({
          ...event,
          colour: learner.colour,
          relatedField: {
            type: 'Class',
            id: c.id
          }
        }))
      )
    );

    const extracurricularEvents = this.guardian.learners.flatMap(learner =>
      learner.extracurriculars.flatMap(e =>
        e.events.map(event => ({
          ...event,
          colour: learner.colour,
          relatedField: {
            type: 'Extracurricular',
            id: e.id
          }
        }))
      )
    );

    return [...schoolEvents, ...classEvents, ...extracurricularEvents];
  }

  getAcademicClasses(): IAcademicClass[] {
    if (!this.guardian) {
      return [];
    }

    const classes: IAcademicClass[] = []

    this.guardian.learners.forEach(learner => {
      classes.push(...learner.classes)
    })

    return classes
  }

  getActivities(): IExtracurricular[] {
    if (!this.guardian) {
      return [];
    }

    const activities: IExtracurricular[] = []

    this.guardian.learners.forEach(learner => {
      activities.push(...learner.extracurriculars,)
    })

    return activities
  }

  getResources(): IResource[] {
    if (!this.guardian) {
      return [];
    }

    const resources: IResource[] = [];

    this.guardian.learners.forEach(learner => {
      learner.classes.forEach(academicClass => {
        resources.push(...academicClass.resources);
        academicClass.tasks.forEach(task => {
          resources.push(...task.resources);
        });
      });
    });

    return resources;
  }

  getDebitOrders() {
    if (!this.guardian) {
      return [];
    }

    return this.guardian.debitOrders;
  }

  getNotifications(): INotification[] {
    if (!this.guardian) {
      return [];
    }

    const notifications: INotification[] = [];

    // Collect all classes from the learners of the guardian
    this.guardian.learners.forEach(learner => {

      learner.attendance.forEach(attend => {
        const notification: INotification = {
          id: faker.string.uuid(), // Using faker to generate UUID
          learnerName: learner.firstname,
          read: false, // Mark as unread
          colour: learner.colour, // Colour assigned based on learner
          icon: 'todo', // Assuming attend-specific icon
          message: ` ${attend.status.toLowerCase()}`, // Custom message with learner's name
          timestamp: attend.date, // Use attend date as timestamp
          url: `/learners/${learner.id}/attendance`, // Dynamic URL for the assessment
          relatedIds: [{ identifier: learner.id, name: 'student' }]
        };
        notifications.push(notification);
      });

      const classes: IAcademicClass[] = learner.classes; // Get classes for the current learner

      classes.forEach(c => {
        // For each assessment, create a notification
        c.assessments.forEach(assessment => {
          const notification: INotification = {
            id: faker.string.uuid(), // Using faker to generate UUID
            learnerName: learner.firstname,
            read: false, // Mark as unread
            colour: learner.colour, // Colour assigned based on learner
            icon: 'group-achievement', // Assuming assessment-specific icon
            message: `'s marks are available`, // Custom message with learner's name
            timestamp: assessment.date, // Use assessment date as timestamp
            url: `/learners/${learner.id}/subjects/marks`, // Dynamic URL for the assessment
            relatedIds: [{ identifier: learner.id, name: 'student' }]
          };
          notifications.push(notification);
        });

        // For each task, create a notification
        c.tasks.forEach(task => {
          const notification: INotification = {
            id: faker.string.uuid(), // Using faker to generate UUID
            learnerName: learner.firstname,
            read: false, // Mark as unread
            colour: learner.colour, // Colour assigned based on learner
            icon: 'checklist-apple', // Assuming task-specific icon
            message: ` has new homework added`, // Custom message with learner's name
            timestamp: task.createDate, // Use task creation date as timestamp
            url: `/learners/${learner.id}/homework/${task.id}`, // Dynamic URL for the task
            relatedIds: [{ identifier: learner.id, name: 'student' }, { identifier: c.id, name: 'class' }]
          };
          notifications.push(notification);
        });
      });
    });

    return notifications;
  }

  // filepath: /Users/jladwig/DVTCode/CurroClient/MyHub/my-hub-frontend/src/app/core/services/faker/global-data.service.ts
  getSchoolNamesAndColours(): Observable<{ schoolName: string; colour: string; }[]> {
    return of(null).pipe(
      delay(1),
      map(() => {
        if (!this.guardian) {
          return [];
        }

        const schoolNamesAndColours = this.guardian.learners.map(learner => ({
          schoolName: learner.school.name,
          colour: learner.colour
        }));
        return schoolNamesAndColours;
      })
    );
  }

}

